import { useNavigate } from 'react-router-dom';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import {
  Scale, Building2, MapPin, Calendar, CheckCircle, Clock,
  AlertCircle, XCircle, ChevronRight, FileSignature
} from 'lucide-react';
import type { RegionalLicense } from '../../types/cultivar';

interface LicenseCardProps {
  license: RegionalLicense;
  userRole: 'licensor' | 'licensee' | 'observer';
}

const STATUS_CONFIG: Record<string, {
  label: string;
  icon: typeof CheckCircle;
  bgColor: string;
  textColor: string;
}> = {
  active: {
    label: 'Active',
    icon: CheckCircle,
    bgColor: 'bg-success-100',
    textColor: 'text-success-700',
  },
  pending_confirmation: {
    label: 'Awaiting Signature',
    icon: Clock,
    bgColor: 'bg-warning-100',
    textColor: 'text-warning-700',
  },
  suspended: {
    label: 'Suspended',
    icon: AlertCircle,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
  },
  terminated: {
    label: 'Terminated',
    icon: XCircle,
    bgColor: 'bg-error-100',
    textColor: 'text-error-700',
  },
  rejected: {
    label: 'Declined',
    icon: XCircle,
    bgColor: 'bg-neutral-100',
    textColor: 'text-neutral-700',
  },
  expired: {
    label: 'Expired',
    icon: Clock,
    bgColor: 'bg-neutral-100',
    textColor: 'text-neutral-600',
  },
  // An unsigned agreement withdrawn because its contract moved to a new
  // version (and usually re-sent on it) or was withdrawn outright.
  superseded: {
    label: 'Withdrawn',
    icon: XCircle,
    bgColor: 'bg-neutral-100',
    textColor: 'text-neutral-500',
  },
};

const TIER_LABELS: Record<string, string> = {
  nursery_master_sublicence: 'Master Sublicence',
  program_master: 'Program Master',
  nursery_propagation: 'Nursery Propagation',
  nursery_sales: 'Nursery Sales',
  orchard_planting: 'Orchard Planting',
  evaluation: 'Evaluation',
};

function LicenseCard({ license, userRole }: LicenseCardProps) {
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const statusConfig = STATUS_CONFIG[license.status] || STATUS_CONFIG.pending_confirmation;
  const StatusIcon = statusConfig.icon;

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    return formatLocalDateInTz(date, timezone);
  };

  const needsSignature = license.status === 'pending_confirmation'
    && userRole === 'licensee'
    && !license.licensee_confirmed;

  const awaitingOtherSide = license.status === 'pending_confirmation'
    && userRole === 'licensor';

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        {/* Pending banner */}
        {needsSignature && (
          <div className="mb-4 -mt-2 -mx-2 px-4 py-3 bg-warning-50 border-b border-warning-200 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSignature className="h-4 w-4 text-warning-600" />
                <span className="text-sm font-medium text-warning-800">
                  Your signature required
                </span>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/cultivar/licences/${license.id}/agreement`)}
              >
                Open agreement
              </Button>
            </div>
          </div>
        )}
        {awaitingOtherSide && (
          <div className="mb-4 -mt-2 -mx-2 px-4 py-3 bg-neutral-50 border-b border-neutral-200 rounded-t-lg">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-neutral-500" />
              <span className="text-sm font-medium text-neutral-700">
                Waiting for {license.licensee_org_name} to sign
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-neutral-900 truncate">
                {license.cultivar_name || (license.program_name ? `${license.program_name} program` : null)}
              </h3>
              {license.cultivar_trade_name && (
                <span className="text-sm text-neutral-500">
                  ({license.cultivar_trade_name})
                </span>
              )}
            </div>
            <p className="text-sm text-primary-600 font-medium">
              {TIER_LABELS[license.license_tier] || license.license_tier}
            </p>
          </div>
          <div className={`
            flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium
            ${statusConfig.bgColor} ${statusConfig.textColor}
          `}>
            <StatusIcon className="h-4 w-4" />
            {statusConfig.label}
          </div>
        </div>

        {/* Suspended / terminated public reason — visible to both parties */}
        {(license.status === 'suspended' || license.status === 'terminated') && (
          license.suspension_reason_public || license.termination_reason_public
        ) && (
          <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm">
            <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">
              Reason given by licensor
            </p>
            <p className="text-amber-900">
              {license.status === 'suspended'
                ? license.suspension_reason_public
                : license.termination_reason_public}
            </p>
          </div>
        )}

        {/* Parties */}
        <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-neutral-100">
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Licensor</p>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-900 truncate">
                {license.licensor_org_name}
              </span>
              {license.licensor_confirmed && (
                <CheckCircle className="h-4 w-4 text-success-500 flex-shrink-0" />
              )}
            </div>
          </div>
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Licensee</p>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-900 truncate">
                {license.licensee_org_name}
              </span>
              {license.licensee_confirmed && (
                <CheckCircle className="h-4 w-4 text-success-500 flex-shrink-0" />
              )}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {(license.region || license.country) && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <span className="text-neutral-600 truncate">
                {license.region || license.country || 'Global'}
              </span>
            </div>
          )}
          {license.license_number && (
            <div className="flex items-center gap-2 text-sm">
              <Scale className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <span className="text-neutral-600 truncate">
                {license.license_number}
              </span>
            </div>
          )}
          {license.start_date && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <span className="text-neutral-600">
                From {formatDate(license.start_date)}
              </span>
            </div>
          )}
          {license.end_date && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <span className="text-neutral-600">
                Until {formatDate(license.end_date)}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/cultivar/licences/${license.id}/agreement`)}
            className="flex-1"
          >
            View agreement
            <ChevronRight className="h-4 w-4" />
          </Button>
          {license.status === 'active' && userRole === 'licensee' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/cultivar/declarations?license_id=${license.id}`)}
            >
              Declarations
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default LicenseCard;
